import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { isPropertyRoute } from "@/lib/contactGate";
import { safeUrl } from "@/lib/media";
import useLocalities from "@/lib/useLocalities";

export default function Footer() {
    const { settings } = useSettings();
    const hideContact = isPropertyRoute(useLocation().pathname);
    const areas = useLocalities();
    if (!settings) return null;
    // Footer is dark: use the dark-background logo if one is set, then the built-in one, and only fall back to the
    // header logo (on a white pill so a dark logo stays visible) when a custom header logo exists without a dark one.
    const badge = safeUrl(settings.logo_dark_url) || safeUrl(settings.logo_url) || "/brand/icon.svg";

    return (
        <footer className="mt-24 bg-[var(--navy)] text-slate-200" data-testid="site-footer">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.7fr_1fr_1fr_1.5fr] gap-10 [&>*]:min-w-0">
                <div className="md:col-span-2 lg:col-span-1">
                    <div className="flex items-center gap-4">
                        <img src={badge} alt="" aria-hidden="true" className="h-16 w-16 object-contain shrink-0" />
                        <div className="min-w-0">
                            <div className="font-serif-display font-semibold text-[28px] leading-none text-white">Elegant Home Advisors</div>
                            <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Your Trusted Partner in Finding Premium Homes</div>
                        </div>
                    </div>
                    <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
                        Elegant Home Advisors specialises in premium residences across Mumbai, Thane, Navi Mumbai and its extended suburbs.
                    </p>
                    <div className="flex gap-3 mt-6">
                        {settings.facebook && <a href={settings.facebook} className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center hover:border-[var(--gold)]" data-testid="footer-facebook"><Facebook size={16} /></a>}
                        {settings.instagram && <a href={settings.instagram} className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center hover:border-[var(--gold)]" data-testid="footer-instagram"><Instagram size={16} /></a>}
                        {settings.linkedin && <a href={settings.linkedin} className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center hover:border-[var(--gold)]" data-testid="footer-linkedin"><Linkedin size={16} /></a>}
                        {settings.youtube && <a href={settings.youtube} className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center hover:border-[var(--gold)]" data-testid="footer-youtube"><Youtube size={16} /></a>}
                    </div>
                </div>

                <div>
                    <div className="overline text-[var(--gold)]">Explore</div>
                    <ul className="mt-5 space-y-2.5 text-slate-300">
                        <li><Link to="/properties" className="hover:text-[var(--gold)]">All Properties</Link></li>
                        <li><Link to="/emi-calculator" className="hover:text-[var(--gold)]">EMI Calculator</Link></li>
                        <li><Link to="/properties?category=Presidential+Properties" className="hover:text-[var(--gold)]">Presidential</Link></li>
                        <li><Link to="/properties?category=Under+Construction" className="hover:text-[var(--gold)]">Under Construction</Link></li>
                        <li><Link to="/properties?category=Ready+to+Move" className="hover:text-[var(--gold)]">Ready to Move</Link></li>
                        <li><Link to="/guides" className="hover:text-[var(--gold)]">Buyer Guides</Link></li>
                        <li><Link to="/contact" className="hover:text-[var(--gold)]">Contact</Link></li>
                        <li><Link to="/about" className="hover:text-[var(--gold)]" data-testid="footer-about">About Us</Link></li>
                    </ul>
                </div>

                {areas.length > 0 && (
                    <div data-testid="footer-areas">
                        <div className="overline text-[var(--gold)]">Popular areas</div>
                        <ul className="mt-5 space-y-2.5 text-slate-300">
                            {areas.slice(0, 8).map((a) => <li key={a.slug}><Link to={`/flats-in-${a.slug}`} className="hover:text-[var(--gold)]">Flats in {a.name}</Link></li>)}
                        </ul>
                    </div>
                )}

                <div>
                    <div className="overline text-[var(--gold)]">Get in touch</div>
                    <ul className="mt-5 space-y-3 text-slate-300">
                        {!hideContact && <li className="flex items-start gap-2"><Phone size={16} className="mt-1 text-[var(--gold)]" /><a href={`tel:${settings.phone}`} className="min-w-0 [overflow-wrap:anywhere]">{settings.phone}</a></li>}
                        {!hideContact && <li className="flex items-start gap-2"><Mail size={16} className="mt-1 text-[var(--gold)]" /><a href={`mailto:${settings.email}`} className="min-w-0 [overflow-wrap:anywhere]">{settings.email}</a></li>}
                        <li className="flex items-start gap-2"><MapPin size={16} className="mt-1 text-[var(--gold)]" /><span className="min-w-0 [overflow-wrap:anywhere]">{settings.address}</span></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-slate-800">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
                    <div className="text-center sm:text-left">© {new Date().getFullYear()} Elegant Home Advisors. All rights reserved.{settings.maharera_number && <> · MahaRERA Reg. No.: <span className="text-slate-300 font-medium" data-testid="footer-maharera">{settings.maharera_number}</span></>}</div>
                    <div className="flex gap-6">
    <Link
        to="/privacy-policy"
        className="hover:text-[var(--gold)]"
    >
        Privacy Policy
    </Link>

    <Link
        to="/terms-of-service"
        className="hover:text-[var(--gold)]"
    >
        Terms of Service
    </Link>
</div>
                </div>
            </div>
        </footer>
    );
}
